import { approachBeats, type GateShape, type Note } from '../core/chart';
import type { Beat, Seconds, Verdict } from '../core/types';

export const PASS_BEATS: Beat = 0.75;
export const FIELD_SCALE = 0.94;
export const INK_WIDTH = 0.022;
export const PULSE_DECAY = 6;
export const PULSE_WIDTH = 0.35;
export const SIGHT_FADE_BEATS: Beat = 0.75;
export const DEPTH_PER_BEAT = 0.55;
export const TILT = 0.45;
export const THICK = 0.3;
export const SHADOW_FLOOR = 0.2;
export const SHADOW_SPREAD = 0.45;
export const QUEUE = 3;

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Incoming {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
  readonly squash: number;
  readonly thick: number;
  readonly alpha: number;
}

export interface Shade {
  readonly alpha: number;
  readonly spread: number;
}

export type Fit = 'seat' | 'gap' | 'squash' | 'fall' | 'pass';

export interface Motion {
  readonly scaleX: number;
  readonly scaleY: number;
  readonly dy: number;
  readonly alpha: number;
}

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function unitOf(view: Viewport): number {
  return (Math.min(view.width, view.height) / 2) * FIELD_SCALE;
}

export const VANISH: Point = { x: 0.35, y: -1.05 };

export function fieldPoint(view: Viewport, x: number, y: number): Point {
  const unit = unitOf(view);
  return { x: view.width / 2 + x * unit, y: view.height / 2 + y * unit };
}

export function depthRate(holdBeats: Beat): number {
  return holdBeats > 0 ? Math.min(DEPTH_PER_BEAT, 1 / holdBeats) : DEPTH_PER_BEAT;
}

export function gateScale(lead: Beat, rate: number): number {
  return lead >= 0 ? 1 / (1 + lead * rate) : 1 - lead * rate;
}

export function incomingAt(note: Note, lead: Beat): Incoming | null {
  const span = approachBeats(note);
  if (!Number.isFinite(lead) || lead > span) return null;
  const alpha = lead >= 0 ? clamp01((span - lead) / SIGHT_FADE_BEATS) : clamp01(1 + lead / PASS_BEATS);
  if (alpha <= 0) return null;
  const scale = lead > 0 ? gateScale(lead, depthRate(note.holdBeats)) : 1;
  const far = 1 - scale;
  return {
    x: note.x + (VANISH.x - note.x) * far,
    y: note.y + (VANISH.y - note.y) * far,
    scale,
    squash: 1 - TILT * far,
    thick: THICK * far,
    alpha,
  };
}

export function shadowAt(note: Note, lead: Beat): Shade | null {
  const disc = incomingAt(note, lead);
  if (disc === null) return null;
  const near = disc.scale * disc.scale;
  return { alpha: disc.alpha * (SHADOW_FLOOR + (1 - SHADOW_FLOOR) * near), spread: SHADOW_SPREAD * (1 - disc.scale) };
}

export function pipsLit(note: Note, beat: Beat): number {
  if (!Number.isFinite(beat)) return 0;
  return Math.max(0, Math.min(note.holdBeats, Math.floor(beat - note.openBeat + 1e-9)));
}

export function clientToField(rect: Rect, clientX: number, clientY: number): Point {
  const unit = unitOf(rect);
  if (unit <= 0) return { x: 0, y: 0 };
  return {
    x: (clientX - rect.left - rect.width / 2) / unit,
    y: (clientY - rect.top - rect.height / 2) / unit,
  };
}

export function beatPulse(beat: Beat): number {
  if (!Number.isFinite(beat) || beat < 0) return 0;
  return Math.exp(-(beat - Math.floor(beat)) * PULSE_DECAY);
}

const SIDES: Record<GateShape, number> = { circle: 0, square: 4, triangle: 3, hex: 6 };

export function shapePoints(shape: GateShape, cx: number, cy: number, radius: number, rotation: number): number[] {
  const sides = SIDES[shape];
  if (sides === 0) return [];
  const points: number[] = [];
  const start = -Math.PI / 2 + (shape === 'square' ? Math.PI / 4 : 0) + rotation;
  for (let i = 0; i < sides; i++) {
    const angle = start + (i * 2 * Math.PI) / sides;
    points.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
  }
  return points;
}

export function fitOf(verdict: Verdict): Fit {
  switch (verdict) {
    case 'perfect':
      return 'seat';
    case 'good':
      return 'gap';
    case 'weak':
      return 'squash';
    case 'flop':
      return 'fall';
    default:
      return 'pass';
  }
}

const STILL: Motion = { scaleX: 1, scaleY: 1, dy: 0, alpha: 1 };
const SEAT_POP = 0.1;
const SEAT_SECONDS = 0.18;
const SEAT_WOBBLE = 0.14;
const SEAT_DAMP = 0.1;
const SEAT_PERIOD = 0.16;
const SQUASH = 0.2;
const SQUASH_SECONDS = 0.28;
const FALL_SECONDS = 0.6;
const FALL_DROP = 0.35;
const FALL_SHRINK = 0.7;
const PASS_SECONDS = 0.4;

export function landingMotion(fit: Fit, age: Seconds, reduced: boolean): Motion {
  const t = (span: number): number => clamp01(age / span);
  switch (fit) {
    case 'seat': {
      if (reduced) return STILL;
      const pop = 1 + SEAT_POP * (1 - t(SEAT_SECONDS));
      const wobble = SEAT_WOBBLE * Math.exp(-age / SEAT_DAMP) * Math.cos((2 * Math.PI * age) / SEAT_PERIOD);
      return { scaleX: pop + wobble, scaleY: pop - wobble, dy: 0, alpha: 1 };
    }
    case 'gap':
      return STILL;
    case 'squash': {
      if (reduced) return STILL;
      const k = SQUASH * (1 - t(SQUASH_SECONDS));
      return { scaleX: 1 + k, scaleY: 1 - k, dy: 0, alpha: 1 };
    }
    case 'fall': {
      const f = t(FALL_SECONDS);
      if (reduced) return { scaleX: 1 - FALL_SHRINK / 2, scaleY: 1 - FALL_SHRINK / 2, dy: 0, alpha: 1 - f };
      const shrink = 1 - FALL_SHRINK * f;
      return { scaleX: shrink, scaleY: shrink, dy: FALL_DROP * f * f, alpha: 1 - f };
    }
    default:
      return { scaleX: 1, scaleY: 1, dy: 0, alpha: 1 - t(PASS_SECONDS) };
  }
}
